import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { sendUserMessage } from '../xstate/app.xstate-utils';

export const PUBLISH_IN_PROGRESS_TOAST_ID = 'publish-in-progress';

/** Status values for solution version (from backend). */
export type SolutionVersionStatus = 'ACTIVE' | 'PUBLISHING' | 'CREATING';

/**
 * Publish attempts whose outcome has already been announced in this tab, keyed by
 * `buildPublishAttemptId` — so a republish, a retry after a failure, and a second publish of
 * the same version each announce again, while an attempt observed twice announces once.
 */
const announcedPublishAttemptIds = new Set<string>();

/**
 * Restores UI state after a publish attempt resolves: the Publish button lock lifted and
 * the in-progress toast gone. Idempotent — safe to call from multiple subscribers.
 *
 * It does not touch the table mode. Under draft-only versioning a publish never puts the
 * editor into view mode, so there is nothing to restore — and forcing `editing` here
 * would override whatever mode the table is legitimately in (loading, a viewer's
 * read-only table).
 */
export function restorePublishUiState(): void {
  tableStore.setSolutionVersionPublishingInProgress(false);
  dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
}

/**
 * The dedup key for one publish attempt.
 *
 * The solution plus the lock's `publishStartedAt` first: the lock is solution-wide and
 * stamped anew by every `acquirePublishLock`, so it names exactly one attempt. NOT the
 * deployment row id (PSTUDIO-4333): the backend upserts one row per
 * `(solutionVersionId, environment)`, so a republish, and a publish after a failure that
 * reuses the failed slot, keep the id — and that second outcome was swallowed as "already
 * announced". The row plus its `deployedAt` (re-stamped by each pipeline run) is the
 * fallback when the lock start is unknown, and the bare solution the last resort.
 */
export function buildPublishAttemptId({
  solutionId,
  publishStartedAt,
  deploymentId,
  deployedAt,
}: {
  solutionId?: string | null;
  publishStartedAt?: string | null;
  deploymentId?: string | null;
  deployedAt?: string | null;
}): string | undefined {
  if (solutionId && publishStartedAt) {
    return `attempt:${solutionId}@${publishStartedAt}`;
  }
  if (deploymentId) {
    return deployedAt ? `${deploymentId}@${deployedAt}` : deploymentId;
  }
  return solutionId ? `attempt:${solutionId}` : undefined;
}

/**
 * Announces how a publish ended and lifts the Publish button lock.
 *
 * UI state is restored unconditionally — even for an attempt already announced, nobody may
 * be left with a disabled button behind a stale in-progress toast — while the toast itself
 * is shown once per attempt.
 *
 * A failed publish gets a toast that does not auto-close: it is the one thing the user has
 * to act on, and before deployment rows existed a coworker was never told at all. So does a
 * `stale` one — a publish whose lock expired unfinished (PSTUDIO-4333): not known to have
 * failed, but the user has to decide whether to publish again.
 */
export function notifyPublishOutcome({
  attemptId,
  outcome,
  message,
}: {
  attemptId?: string;
  outcome: 'success' | 'failure' | 'stale';
  message: string;
}): void {
  restorePublishUiState();

  if (!attemptId || announcedPublishAttemptIds.has(attemptId)) return;
  announcedPublishAttemptIds.add(attemptId);

  sendUserMessage({
    type:
      outcome === 'success'
        ? 'success'
        : outcome === 'stale'
          ? 'warning'
          : 'error',
    text: message,
    autoClose: outcome === 'success' ? 5000 : false,
  });
}

/**
 * How long the publisher's tab waits, after its publish was accepted, for any sign of the
 * attempt — PUBLISHING, or a deployment row changed since the click — before it releases
 * the button it raised. Well beyond the live query's poll; see `beginLocalPublishAttempt`.
 */
export const LOCAL_PUBLISH_ATTEMPT_WAIT_MS = 15_000;

/** The attempt this tab started, per solution — see `beginLocalPublishAttempt`. */
type LocalPublishAttempt = {
  /** Every deployment row as it stood when the user confirmed: id → its own `updatedAt`. */
  baseline: Map<string, string | null>;
  /** When the backend accepted the publish (this tab's clock; only ever compared to itself). */
  acceptedAt?: number;
};

const localPublishAttempts = new Map<string, LocalPublishAttempt>();
const localPublishAttemptListeners = new Set<() => void>();

const notifyLocalPublishAttemptListeners = () => {
  localPublishAttemptListeners.forEach((listener) => listener());
};

/**
 * Records that THIS tab is publishing `solutionId`, with a snapshot of its deployment rows
 * (PSTUDIO-4333).
 *
 * Everyone else learns of a publish from the solution going PUBLISHING. But the lock can be
 * taken and released between two polls of the live query — a publish that fails fast — and
 * then no tab ever sees it. The publisher's tab would keep the button it raised disabled
 * until the user switched solutions. It is the one tab that knows it published, so it keeps
 * what the rows looked like at the click: the row that is new or changed since is its
 * attempt. That compares the backend's timestamps with the backend's own, so no client
 * clock is involved. Called before the mutation is sent, so the snapshot predates any row
 * the attempt writes.
 */
export function beginLocalPublishAttempt({
  solutionId,
  rows,
}: {
  solutionId: string | null | undefined;
  rows: { id: string; updatedAt?: string | null }[] | null | undefined;
}): void {
  if (!solutionId) return;
  localPublishAttempts.set(solutionId, {
    baseline: new Map(
      (rows ?? []).map((row) => [row.id, row.updatedAt ?? null])
    ),
  });
  notifyLocalPublishAttemptListeners();
}

/** The backend accepted this tab's publish — from here the wait for a sign of it runs. */
export function markLocalPublishAccepted(
  solutionId: string | null | undefined,
  now: number = Date.now()
): void {
  const attempt = solutionId ? localPublishAttempts.get(solutionId) : undefined;
  if (!attempt) return;
  attempt.acceptedAt = now;
  notifyLocalPublishAttemptListeners();
}

/** Forgets this tab's attempt: resolved, rejected, or handed to the normal path. */
export function clearLocalPublishAttempt(
  solutionId: string | null | undefined
): void {
  if (solutionId && localPublishAttempts.delete(solutionId)) {
    notifyLocalPublishAttemptListeners();
  }
}

/** This tab's attempt on `solutionId`, if it has one. */
export function readLocalPublishAttempt(
  solutionId: string | null | undefined
): Readonly<LocalPublishAttempt> | undefined {
  return solutionId ? localPublishAttempts.get(solutionId) : undefined;
}

/** Called whenever this tab's attempts change; returns the unsubscribe. */
export function subscribeLocalPublishAttempts(
  listener: () => void
): () => void {
  localPublishAttemptListeners.add(listener);
  return () => {
    localPublishAttemptListeners.delete(listener);
  };
}
